import React, {useState} from 'react';

import {
  AntibodyTagSchemaQuery,
  AntibodyTagSchemaQuery_bioentity_BasicFolderItemNode as AntibodyTagSchemaQueryBFINode,
  AntibodyTagSchemaQueryVariables,
} from 'antibodies/actions/__generated/AntibodyTagSchemaQuery';
import {ANTIBODY_TAG_SCHEMA_QUERY, onUpdateAntibodyVersion} from 'antibodies/actions/AntibodyActionUtil';
import AntibodyComponentInputs, {DomainWithProtein} from 'antibodies/actions/AntibodyComponentInputs';
import {editAntibodyStructure} from 'antibodies/AntibodyDAO';
import {AntibodyWithVersion} from 'antibodies/AntibodyTypedefs';
import {AntibodyVersionQuery_antibody_AntibodyNode_version_AntibodyVersionNode_domains_AntibodyDomainNode as AntibodyDomainNode} from 'antibodies/hooks/__generated/AntibodyVersionQuery';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Modal from 'ui-library/modals/Modal';
import {CenteredSpinner} from 'ui-library/Spinner';

interface AntibodyEditModalProps {
  antibody: AntibodyWithVersion;
  onRequestClose: () => void;
}

function getDomainsWithProteinByLabel(domains: Array<AntibodyDomainNode>): Record<string, DomainWithProtein> {
  return Object.fromEntries(
    domains.map((domain) => [
      domain.label,
      {
        ...domain,
        proteinId:
          domain.entity == null
            ? null
            : isInaccessible(domain.entity)
              ? // Still want to add the id to the map even if it's inaccessible.
                domain.entity.invalidId
              : domain.entity.id,
      },
    ])
  );
}

/**
 * Modal for editing the domains of an antibody entity.
 */
export default function AntibodyEditModal({antibody, onRequestClose}: AntibodyEditModalProps): JSX.Element {
  const [saving, setSaving] = useState(false);
  const currentDomainsWithProteinByLabel = getDomainsWithProteinByLabel(antibody.version.domains);

  const [updatedDomainsWithProteinByLabel, setUpdatedDomainsWithProteinByLabel] = useState<
    Record<string, DomainWithProtein>
  >(currentDomainsWithProteinByLabel);

  const schemaQueryResult = useBenchlingQuery<AntibodyTagSchemaQuery, AntibodyTagSchemaQueryVariables>(
    ANTIBODY_TAG_SCHEMA_QUERY,
    {variables: {id: antibody.id}}
  );
  if (schemaQueryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }
  const invalidSchema =
    schemaQueryResult.type === QueryResultType.ERROR ||
    schemaQueryResult.data.bioentity == null ||
    isInaccessible(schemaQueryResult.data.bioentity) ||
    schemaQueryResult.data.bioentity.__typename !== 'BasicFolderItemNode' ||
    schemaQueryResult.data.bioentity.tagSchema == null;

  const canSubmit =
    !invalidSchema &&
    !saving &&
    // At least one domain has been changed
    antibody.format.domains.some(
      (domain) =>
        currentDomainsWithProteinByLabel[domain.label]?.proteinId !==
        updatedDomainsWithProteinByLabel[domain.label]?.proteinId
    ) &&
    antibody.format.domains.every(
      // There is a domain specified for each required slot.
      (domain) => domain.optional || updatedDomainsWithProteinByLabel[domain.label]?.proteinId != null
    );

  const onRequestSubmit = async (): Promise<void> => {
    setSaving(true);
    const domains = Object.values(updatedDomainsWithProteinByLabel)
      .filter((domainWithProtein) => domainWithProtein.proteinId != null)
      .map((domainWithProtein) => ({
        label: domainWithProtein.label,
        aaSequenceId: domainWithProtein.proteinId!,
      }));
    try {
      const {versionId, tags} = await editAntibodyStructure(antibody.id, antibody.currentVersionId, {
        domains,
      });
      onUpdateAntibodyVersion(antibody.id, versionId, tags);
      onRequestClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      buttons={[{text: saving ? 'Saving...' : 'Save', submit: true, disabled: !canSubmit}]}
      expandHeight={true}
      large={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      title="Edit protein"
    >
      {invalidSchema ? (
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Failed to load protein data" />
      ) : (
        <AntibodyComponentInputs
          // The type assertions for schemaQueryResult are already validated by invalidSchema being false.
          antibodySchemaId={(schemaQueryResult.data.bioentity as AntibodyTagSchemaQueryBFINode).tagSchema!.id}
          disabled={saving}
          format={antibody.format}
          domainsWithProteinByLabel={updatedDomainsWithProteinByLabel}
          setDomainsWithProteinByLabel={setUpdatedDomainsWithProteinByLabel}
        />
      )}
    </Modal>
  );
}
